import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useMemo } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import { z } from 'zod'

import CopyButtonWithLabel from '@/common/CopyButtonWithLabel'
import GradientWrapper from '@/common/GradientWrapper'
import { downloadJson } from '@/helpers'
import ProposalPreview from '@/pages/Multisig/components/ProposalPreview'
import { useSubmitMultisigProposal } from '@/pages/Multisig/CreateProposal/hooks/submit-multisig-proposal'
import { UiButton } from '@/ui/UiButton'
import { UiCheckbox } from '@/ui/UiCheckbox'
import { UiInput } from '@/ui/UiInput'
import { UiLabel } from '@/ui/UiLabel'

import { buildAddTxToStopListMessage } from './stoplistMessageBuilder'

const addTxToStopListSchema = z.object({
  creator: z.string().min(1, 'Creator address is required'),
  transaction: z.object({
    depositChainId: z.string().min(1, 'Deposit chain ID is required'),
    depositTxHash: z.string().min(1, 'Deposit transaction hash is required'),
    depositTxIndex: z.string().min(1, 'Deposit transaction index is required'),
    depositBlock: z.string().min(1, 'Deposit block is required'),
    depositToken: z.string().min(1, 'Deposit token is required'),
    depositAmount: z.string().min(1, 'Deposit amount is required'),
    depositor: z.string().min(1, 'Depositor address is required'),
    receiver: z.string().min(1, 'Receiver address is required'),
    withdrawalChainId: z.string().min(1, 'Withdrawal chain ID is required'),
    withdrawalTxHash: z.string().default(''),
    withdrawalToken: z.string().min(1, 'Withdrawal token is required'),
    signature: z.string().default(''),
    isWrapped: z.boolean(),
    withdrawalAmount: z.string().min(1, 'Withdrawal amount is required'),
    commissionAmount: z.string().min(1, 'Commission amount is required'),
    txData: z.string().default(''),
    referralId: z.string().default('0'),
    merkleProof: z.string().default(''),
    epochId: z.string().default('0'),
  }),
})

export default function AddTxToStopListForm() {
  const { account } = useParams<{ account: string }>()

  const form = useForm({
    resolver: zodResolver(addTxToStopListSchema),
    mode: 'onChange',
    reValidateMode: 'onChange',
    shouldFocusError: true,
    defaultValues: {
      creator: '',
      transaction: {
        depositChainId: '',
        depositTxHash: '',
        depositTxIndex: '',
        depositBlock: '',
        depositToken: '',
        depositAmount: '',
        depositor: '',
        receiver: '',
        withdrawalChainId: '',
        withdrawalTxHash: '',
        withdrawalToken: '',
        signature: '',
        isWrapped: false,
        withdrawalAmount: '',
        commissionAmount: '',
        txData: '',
        referralId: '0',
        merkleProof: '',
        epochId: '0',
      },
    },
  })

  const submitProposalTx = useSubmitMultisigProposal({ group: account ?? '' })

  useEffect(() => {
    form.setValue('creator', account ?? '', { shouldValidate: true })
  }, [account, form])

  const watchedValues = useWatch({ control: form.control })

  const messagePayload = useMemo(() => {
    if (!form.formState.isValid || !watchedValues) return null

    try {
      return buildAddTxToStopListMessage({
        creator: watchedValues.creator ?? '',
        transaction: {
          depositChainId: watchedValues.transaction?.depositChainId ?? '',
          depositTxHash: watchedValues.transaction?.depositTxHash ?? '',
          depositTxIndex: watchedValues.transaction?.depositTxIndex ?? '',
          depositBlock: watchedValues.transaction?.depositBlock ?? '',
          depositToken: watchedValues.transaction?.depositToken ?? '',
          depositAmount: watchedValues.transaction?.depositAmount ?? '',
          depositor: watchedValues.transaction?.depositor ?? '',
          receiver: watchedValues.transaction?.receiver ?? '',
          withdrawalChainId: watchedValues.transaction?.withdrawalChainId ?? '',
          withdrawalTxHash: watchedValues.transaction?.withdrawalTxHash ?? '',
          withdrawalToken: watchedValues.transaction?.withdrawalToken ?? '',
          signature: watchedValues.transaction?.signature ?? '',
          isWrapped: watchedValues.transaction?.isWrapped ?? false,
          withdrawalAmount: watchedValues.transaction?.withdrawalAmount ?? '',
          commissionAmount: watchedValues.transaction?.commissionAmount ?? '',
          txData: watchedValues.transaction?.txData ?? '',
          referralId: watchedValues.transaction?.referralId ?? '0',
          merkleProof: watchedValues.transaction?.merkleProof ?? '',
          epochId: watchedValues.transaction?.epochId ?? '0',
        },
      })
    } catch {
      return null
    }
  }, [form.formState.isValid, watchedValues])

  const generatedJson = useMemo(() => {
    if (!messagePayload) return null
    return JSON.stringify(messagePayload, null, 2)
  }, [messagePayload])

  const isSubmitting = submitProposalTx.isPending
  const isSubmitDisabled = !form.formState.isValid || isSubmitting

  return (
    <div className='space-y-6'>
      <div className='space-y-2'>
        <h3 className='text-foreground text-lg font-semibold'>
          Add Transaction to Stop List
        </h3>
        <p className='text-muted-foreground text-sm'>
          Fill in the form to add a transaction to the stop list
        </p>
      </div>

      <form
        onSubmit={event => event.preventDefault()}
        className='flex flex-col gap-6'
      >
        <div className='flex flex-col gap-3'>
          <UiLabel htmlFor='creator'>Creator Address</UiLabel>
          <UiInput
            id='creator'
            {...form.register('creator')}
            placeholder='Connect wallet'
            readOnly
            disabled={isSubmitting}
            className='bg-input border-border h-12 w-full rounded-lg border px-3 py-2 text-sm outline-none disabled:opacity-60'
          />
          {form.formState.errors.creator && (
            <p className='text-destructive text-sm'>
              {form.formState.errors.creator.message}
            </p>
          )}
        </div>

        <div className='border-border space-y-3 rounded-lg border p-3'>
          <h4 className='text-foreground font-medium'>Transaction Details</h4>

          <div className='mt-5 grid grid-cols-1 gap-3 md:grid-cols-2'>
            <div className='flex flex-col gap-2'>
              <UiLabel htmlFor='transaction.depositChainId'>
                Deposit Chain ID
              </UiLabel>
              <GradientWrapper>
                <UiInput
                  id='transaction.depositChainId'
                  {...form.register('transaction.depositChainId')}
                  placeholder='ethereum'
                  disabled={isSubmitting}
                  className='bg-input text-foreground border-border h-12 w-full rounded-lg border px-3 py-2 text-sm outline-none disabled:opacity-60'
                />
              </GradientWrapper>
              {form.formState.errors.transaction?.depositChainId && (
                <p className='text-destructive text-sm'>
                  {form.formState.errors.transaction.depositChainId.message}
                </p>
              )}
            </div>

            <div className='flex flex-col gap-2'>
              <UiLabel htmlFor='transaction.depositTxHash'>
                Deposit Tx Hash
              </UiLabel>
              <GradientWrapper>
                <UiInput
                  id='transaction.depositTxHash'
                  {...form.register('transaction.depositTxHash')}
                  placeholder='0x...'
                  disabled={isSubmitting}
                  className='bg-input text-foreground border-border h-12 w-full rounded-lg border px-3 py-2 text-sm outline-none disabled:opacity-60'
                />
              </GradientWrapper>
              {form.formState.errors.transaction?.depositTxHash && (
                <p className='text-destructive text-sm'>
                  {form.formState.errors.transaction.depositTxHash.message}
                </p>
              )}
            </div>

            <div className='flex flex-col gap-2'>
              <UiLabel htmlFor='transaction.depositTxIndex'>
                Deposit Tx Index
              </UiLabel>
              <GradientWrapper>
                <UiInput
                  id='transaction.depositTxIndex'
                  {...form.register('transaction.depositTxIndex')}
                  placeholder='0'
                  disabled={isSubmitting}
                  className='bg-input text-foreground border-border h-12 w-full rounded-lg border px-3 py-2 text-sm outline-none disabled:opacity-60'
                />
              </GradientWrapper>
              {form.formState.errors.transaction?.depositTxIndex && (
                <p className='text-destructive text-sm'>
                  {form.formState.errors.transaction.depositTxIndex.message}
                </p>
              )}
            </div>

            <div className='flex flex-col gap-2'>
              <UiLabel htmlFor='transaction.depositBlock'>
                Deposit Block
              </UiLabel>
              <GradientWrapper>
                <UiInput
                  id='transaction.depositBlock'
                  {...form.register('transaction.depositBlock')}
                  placeholder='12345678'
                  disabled={isSubmitting}
                  className='bg-input text-foreground border-border h-12 w-full rounded-lg border px-3 py-2 text-sm outline-none disabled:opacity-60'
                />
              </GradientWrapper>
              {form.formState.errors.transaction?.depositBlock && (
                <p className='text-destructive text-sm'>
                  {form.formState.errors.transaction.depositBlock.message}
                </p>
              )}
            </div>

            <div className='flex flex-col gap-2'>
              <UiLabel htmlFor='transaction.depositToken'>
                Deposit Token
              </UiLabel>
              <GradientWrapper>
                <UiInput
                  id='transaction.depositToken'
                  {...form.register('transaction.depositToken')}
                  placeholder='0x...'
                  disabled={isSubmitting}
                  className='bg-input text-foreground border-border h-12 w-full rounded-lg border px-3 py-2 text-sm outline-none disabled:opacity-60'
                />
              </GradientWrapper>
              {form.formState.errors.transaction?.depositToken && (
                <p className='text-destructive text-sm'>
                  {form.formState.errors.transaction.depositToken.message}
                </p>
              )}
            </div>

            <div className='flex flex-col gap-2'>
              <UiLabel htmlFor='transaction.depositAmount'>
                Deposit Amount
              </UiLabel>
              <GradientWrapper>
                <UiInput
                  id='transaction.depositAmount'
                  {...form.register('transaction.depositAmount')}
                  placeholder='1000000000000000000'
                  disabled={isSubmitting}
                  className='bg-input text-foreground border-border h-12 w-full rounded-lg border px-3 py-2 text-sm outline-none disabled:opacity-60'
                />
              </GradientWrapper>
              {form.formState.errors.transaction?.depositAmount && (
                <p className='text-destructive text-sm'>
                  {form.formState.errors.transaction.depositAmount.message}
                </p>
              )}
            </div>

            <div className='flex flex-col gap-2'>
              <UiLabel htmlFor='transaction.depositor'>
                Depositor Address
              </UiLabel>
              <GradientWrapper>
                <UiInput
                  id='transaction.depositor'
                  {...form.register('transaction.depositor')}
                  placeholder='0x...'
                  disabled={isSubmitting}
                  className='bg-input text-foreground border-border h-12 w-full rounded-lg border px-3 py-2 text-sm outline-none disabled:opacity-60'
                />
              </GradientWrapper>
              {form.formState.errors.transaction?.depositor && (
                <p className='text-destructive text-sm'>
                  {form.formState.errors.transaction.depositor.message}
                </p>
              )}
            </div>

            <div className='flex flex-col gap-2'>
              <UiLabel htmlFor='transaction.receiver'>Receiver Address</UiLabel>
              <GradientWrapper>
                <UiInput
                  id='transaction.receiver'
                  {...form.register('transaction.receiver')}
                  placeholder='bridge1...'
                  disabled={isSubmitting}
                  className='bg-input text-foreground border-border h-12 w-full rounded-lg border px-3 py-2 text-sm outline-none disabled:opacity-60'
                />
              </GradientWrapper>
              {form.formState.errors.transaction?.receiver && (
                <p className='text-destructive text-sm'>
                  {form.formState.errors.transaction.receiver.message}
                </p>
              )}
            </div>

            <div className='flex flex-col gap-2'>
              <UiLabel htmlFor='transaction.withdrawalChainId'>
                Withdrawal Chain ID
              </UiLabel>
              <GradientWrapper>
                <UiInput
                  id='transaction.withdrawalChainId'
                  {...form.register('transaction.withdrawalChainId')}
                  placeholder='bridgeless'
                  disabled={isSubmitting}
                  className='bg-input text-foreground border-border h-12 w-full rounded-lg border px-3 py-2 text-sm outline-none disabled:opacity-60'
                />
              </GradientWrapper>
              {form.formState.errors.transaction?.withdrawalChainId && (
                <p className='text-destructive text-sm'>
                  {form.formState.errors.transaction.withdrawalChainId.message}
                </p>
              )}
            </div>

            <div className='flex flex-col gap-2'>
              <UiLabel htmlFor='transaction.withdrawalToken'>
                Withdrawal Token
              </UiLabel>
              <GradientWrapper>
                <UiInput
                  id='transaction.withdrawalToken'
                  {...form.register('transaction.withdrawalToken')}
                  placeholder='abridge'
                  disabled={isSubmitting}
                  className='bg-input text-foreground border-border h-12 w-full rounded-lg border px-3 py-2 text-sm outline-none disabled:opacity-60'
                />
              </GradientWrapper>
              {form.formState.errors.transaction?.withdrawalToken && (
                <p className='text-destructive text-sm'>
                  {form.formState.errors.transaction.withdrawalToken.message}
                </p>
              )}
            </div>

            <div className='flex flex-col gap-2'>
              <UiLabel htmlFor='transaction.withdrawalAmount'>
                Withdrawal Amount
              </UiLabel>
              <GradientWrapper>
                <UiInput
                  id='transaction.withdrawalAmount'
                  {...form.register('transaction.withdrawalAmount')}
                  placeholder='1000000000000000000'
                  disabled={isSubmitting}
                  className='bg-input text-foreground border-border h-12 w-full rounded-lg border px-3 py-2 text-sm outline-none disabled:opacity-60'
                />
              </GradientWrapper>
              {form.formState.errors.transaction?.withdrawalAmount && (
                <p className='text-destructive text-sm'>
                  {form.formState.errors.transaction.withdrawalAmount.message}
                </p>
              )}
            </div>

            <div className='flex flex-col gap-2'>
              <UiLabel htmlFor='transaction.commissionAmount'>
                Commission Amount
              </UiLabel>
              <GradientWrapper>
                <UiInput
                  id='transaction.commissionAmount'
                  {...form.register('transaction.commissionAmount')}
                  placeholder='0'
                  disabled={isSubmitting}
                  className='bg-input text-foreground border-border h-12 w-full rounded-lg border px-3 py-2 text-sm outline-none disabled:opacity-60'
                />
              </GradientWrapper>
              {form.formState.errors.transaction?.commissionAmount && (
                <p className='text-destructive text-sm'>
                  {form.formState.errors.transaction.commissionAmount.message}
                </p>
              )}
            </div>

            <div className='flex flex-col gap-2 md:col-span-2'>
              <div className='flex items-center space-x-2'>
                <UiCheckbox
                  id='transaction.isWrapped'
                  checked={form.watch('transaction.isWrapped')}
                  onCheckedChange={checked =>
                    form.setValue('transaction.isWrapped', checked === true)
                  }
                  disabled={isSubmitting}
                />
                <UiLabel
                  htmlFor='transaction.isWrapped'
                  className='cursor-pointer'
                >
                  Is Wrapped Token
                </UiLabel>
              </div>
            </div>
          </div>
        </div>

        <div className='border-border space-y-3 rounded-lg border p-3'>
          <h4 className='text-foreground font-medium'>Optional Fields</h4>

          <div className='mt-5 grid grid-cols-1 gap-3 md:grid-cols-2'>
            <div className='flex flex-col gap-2'>
              <UiLabel htmlFor='transaction.withdrawalTxHash'>
                Withdrawal Tx Hash
              </UiLabel>
              <GradientWrapper>
                <UiInput
                  id='transaction.withdrawalTxHash'
                  {...form.register('transaction.withdrawalTxHash')}
                  placeholder='Optional'
                  disabled={isSubmitting}
                  className='bg-input text-foreground border-border h-12 w-full rounded-lg border px-3 py-2 text-sm outline-none disabled:opacity-60'
                />
              </GradientWrapper>
            </div>

            <div className='flex flex-col gap-2'>
              <UiLabel htmlFor='transaction.signature'>Signature</UiLabel>
              <GradientWrapper>
                <UiInput
                  id='transaction.signature'
                  {...form.register('transaction.signature')}
                  placeholder='Optional'
                  disabled={isSubmitting}
                  className='bg-input text-foreground border-border h-12 w-full rounded-lg border px-3 py-2 text-sm outline-none disabled:opacity-60'
                />
              </GradientWrapper>
            </div>

            <div className='flex flex-col gap-2'>
              <UiLabel htmlFor='transaction.txData'>Transaction Data</UiLabel>
              <GradientWrapper>
                <UiInput
                  id='transaction.txData'
                  {...form.register('transaction.txData')}
                  placeholder='Optional'
                  disabled={isSubmitting}
                  className='bg-input text-foreground border-border h-12 w-full rounded-lg border px-3 py-2 text-sm outline-none disabled:opacity-60'
                />
              </GradientWrapper>
            </div>

            <div className='flex flex-col gap-2'>
              <UiLabel htmlFor='transaction.merkleProof'>Merkle Proof</UiLabel>
              <GradientWrapper>
                <UiInput
                  id='transaction.merkleProof'
                  {...form.register('transaction.merkleProof')}
                  placeholder='Optional'
                  disabled={isSubmitting}
                  className='bg-input text-foreground border-border h-12 w-full rounded-lg border px-3 py-2 text-sm outline-none disabled:opacity-60'
                />
              </GradientWrapper>
            </div>

            <div className='flex flex-col gap-2'>
              <UiLabel htmlFor='transaction.referralId'>Referral ID</UiLabel>
              <GradientWrapper>
                <UiInput
                  id='transaction.referralId'
                  {...form.register('transaction.referralId')}
                  placeholder='0'
                  disabled={isSubmitting}
                  type='number'
                  min='0'
                  className='bg-input text-foreground border-border h-12 w-full rounded-lg border px-3 py-2 text-sm outline-none disabled:opacity-60'
                />
              </GradientWrapper>
            </div>

            <div className='flex flex-col gap-2'>
              <UiLabel htmlFor='transaction.epochId'>Epoch ID</UiLabel>
              <GradientWrapper>
                <UiInput
                  id='transaction.epochId'
                  {...form.register('transaction.epochId')}
                  placeholder='0'
                  disabled={isSubmitting}
                  type='number'
                  min='0'
                  className='bg-input text-foreground border-border h-12 w-full rounded-lg border px-3 py-2 text-sm outline-none disabled:opacity-60'
                />
              </GradientWrapper>
            </div>
          </div>
        </div>
      </form>

      {messagePayload && <ProposalPreview messages={messagePayload.messages} />}

      <div className='flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'>
        <div className='flex flex-wrap items-center gap-2'>
          {generatedJson && (
            <>
              <CopyButtonWithLabel
                value={generatedJson}
                label='Copy JSON'
                copiedLabel='Copied'
                tooltip='Copy JSON'
              />
              <UiButton
                type='button'
                variant='outline'
                size='sm'
                onClick={() =>
                  downloadJson(
                    messagePayload,
                    'add-tx-to-stoplist-message.json',
                  )
                }
                disabled={isSubmitting}
              >
                Download JSON
              </UiButton>
            </>
          )}
        </div>
        <UiButton
          type='button'
          disabled={isSubmitDisabled}
          onClick={form.handleSubmit(values => {
            const messageObject = buildAddTxToStopListMessage({
              creator: values.creator,
              transaction: {
                depositChainId: values.transaction.depositChainId,
                depositTxHash: values.transaction.depositTxHash,
                depositTxIndex: values.transaction.depositTxIndex,
                depositBlock: values.transaction.depositBlock,
                depositToken: values.transaction.depositToken,
                depositAmount: values.transaction.depositAmount,
                depositor: values.transaction.depositor,
                receiver: values.transaction.receiver,
                withdrawalChainId: values.transaction.withdrawalChainId,
                withdrawalTxHash: values.transaction.withdrawalTxHash ?? '',
                withdrawalToken: values.transaction.withdrawalToken,
                signature: values.transaction.signature ?? '',
                isWrapped: values.transaction.isWrapped,
                withdrawalAmount: values.transaction.withdrawalAmount,
                commissionAmount: values.transaction.commissionAmount,
                txData: values.transaction.txData ?? '',
                referralId: values.transaction.referralId ?? '0',
                merkleProof: values.transaction.merkleProof ?? '',
                epochId: values.transaction.epochId ?? '0',
              },
            })
            submitProposalTx.mutate({
              rawJsonText: JSON.stringify(messageObject, null, 2),
            })
          })}
        >
          {isSubmitting ? 'Submitting…' : 'Submit proposal'}
        </UiButton>
      </div>
    </div>
  )
}
